import { Injectable } from '@angular/core';

interface CookieOptions {
  days?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

@Injectable({
  providedIn: 'root',
})

export class CookiesService {

  private defaultOptions: CookieOptions = {
    days: 30,
    path: '/',
    secure: true,
    sameSite: 'Strict'
  };

  setCookie(name: string, value: string, options?: CookieOptions): void {
    const opts = { ...this.defaultOptions, ...options };
    const expires = new Date();
    expires.setTime(expires.getTime() + (opts.days || 0) * 24 * 60 * 60 * 1000);

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    cookieString += `;expires=${expires.toUTCString()}`;
    cookieString += `;path=/`; // Make sure path is set

    if (window.location.protocol === 'https:') {
      cookieString += ';secure';  // Add secure flag for HTTPS
    }

    // console.log('Final cookie string:', cookieString); // Debug log
    document.cookie = cookieString;
  }

  getCookie(name: string): string | null {
    try {
      const decodedCookie = decodeURIComponent(document.cookie);
      const cookies = decodedCookie.split(';');

      for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.split('=').map(c => c.trim());
        if (cookieName === encodeURIComponent(name)) {
          return cookieValue;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting cookie:', error);
      return null;
    }
  }

  deleteCookie(name: string, options?: Partial<CookieOptions>): void {
    // When deleting a cookie, we need to use the same path/domain it was set with
    const opts = { ...this.defaultOptions, ...options, days: -1 };
    this.setCookie(name, '', opts);
  }

  getAllCookies(): { [key: string]: string } {
    const result: { [key: string]: string } = {};

    // console.log('Raw cookie string:', document.cookie);  // Debug the raw cookie string

    if (document.cookie) {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        // Improved splitting and trimming
        let [name, ...valueParts] = cookie.split('=');
        name = name.trim();
        const value = valueParts.join('=').trim(); // Handle values that might contain '='

        if (name && value) {
          result[name] = decodeURIComponent(value);
        }
      }
    }

    // console.log('Parsed cookies:', result);  // Debug the parsed result

    return result;
  }

  clearAllCookies(): void {
    const cookies = this.getAllCookies();
    const domains = ['', '.emptylegs.corp-jet.world', window.location.hostname];
    const paths = ['/', '/subpath'];

    Object.keys(cookies).forEach(name => {
      domains.forEach(domain => {
        paths.forEach(path => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? `; domain=${domain}` : ''}; secure; samesite=strict`;
        });
      });
    });
  }

  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }
}